import { NextUp } from 'app/blocks/homepage/next-up'
import React, { useCallback, useMemo } from 'react'
import { ContentCarousel } from 'app/components/content/ContentCarousel'
import { useCarouselController } from 'app/hooks/use-carousel-controller'
import Section from 'app/components/structure/Section'
import { XStack, YStack } from '@mythical/ui'
import {
  IconChevronLeft,
  IconChevronRight,
} from 'app/components/primitive/Icon'
import { Button } from 'app/components/primitive/Button'
import ContentEpisodeListItem, {
  ContentEpisodeListItemProps,
} from 'app/components/content/ContentEpisodesList/ContentEpisodesListItem'

export const NextUpBlock: React.FC<{ block: NextUp }> = ({ block }) => {
  const { title, content, collection } = block || {}
  const controller = useCarouselController()

  if (!content || content?.length === 0) {
    return null
  }

  const contentList: ContentEpisodeListItemProps[] = useMemo(
    () =>
      content.map((item, i) => {
        const series = collection.series?.find((series) =>
          series.episodes?.find((episode) => episode.content === item.id),
        )

        const episode = series?.episodes?.find(
          (episode) => episode.content === item.id,
        )
        const index = series?.episodes?.findIndex(
          (episode) => episode.content === item.id,
        )

        const itemIndex = index ? index + 1 : i + 1

        return {
          ...item,
          href: item.href,
          episodeNumber: episode?.episodeNumber || itemIndex,
          episodeName: episode?.episodeName,
          episodePart: episode?.episodePart,
        }
      }),
    [content, collection],
  )

  const renderItem = useCallback(
    (item: ContentEpisodeListItemProps) => <ContentEpisodeListItem {...item} />,
    [],
  )

  const keyExtractor = useCallback(
    (item: ContentEpisodeListItemProps) => item.id,
    [],
  )

  return (
    <Section
      paddedX
      title={title || 'Next up in'}
      $gtSm={{ overflow: 'hidden' }}
      titleHighlight={collection.name}
      customCta={
        <XStack gap="$2" $sm={{ dsp: 'none' }}>
          <Button
            theme="white"
            onPress={controller.scrollToPrevious}
            disabled={!controller.canScrollPrevious}
            aria-label="Previous"
            icon={<IconChevronLeft size={16}  />}
            size="$3.5"
            circular
          />
          <Button
            theme="white"
            onPress={controller.scrollToNext}
            disabled={!controller.canScrollNext}
            aria-label="Next"
            circular
            size="$3.5"
            icon={<IconChevronRight size={16}  />}
          />
        </XStack>
      }
    >
      <YStack $gtSm={{ overflow: 'hidden' }}>
        <ContentCarousel
          items={contentList}
          renderItem={renderItem}
          keyExtractor={keyExtractor}
          controllerRef={controller.carouselRef}
        />
      </YStack>
    </Section>
  )
}
